import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavBar, Tabs, Toast, Skeleton, Image, Empty, Button, Field, Cell, Tag, Dialog, Popup, ImagePreview } from 'react-vant';
import { ArrowLeft, BullhornO, PhotoO, MedalO, CalendarO, ChatO, Edit } from '@react-vant/icons';
import { 
    getNotifications, 
    getAlbum, 
    getStudentEvaluations, 
    getStudentAttendance, 
    applyForLeave, 
    getMyLeaveRequests,
    replyToEvaluation,
    getSessionDetail
} from '../../api/club';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const ClubSpace = () => {
  const { sessionId, studentId } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector(state => state.user);
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  const [data, setData] = useState({
    notifications: [],
    photos: [],
    evaluations: [],
    attendance: [],
    leaveRequests: []
  });

  // 请假相关
  const [showLeavePopup, setShowLeavePopup] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
    reason: ''
  });
  const [submittingLeave, setSubmittingLeave] = useState(false);

  // 回复相关
  const [showReplyPopup, setShowReplyPopup] = useState(false);
  const [currentEval, setCurrentEval] = useState(null);
  const [replyText, setReplyText] = useState('');

  const fetchData = async () => {
    if (!sessionId || !studentId) return;
    setLoading(true);
    try {
      const [detail, notifs, photos, evals, attn, leaves] = await Promise.all([
        getSessionDetail(sessionId),
        getNotifications(sessionId),
        getAlbum(sessionId),
        getStudentEvaluations(studentId, sessionId),
        getStudentAttendance(studentId, sessionId),
        getMyLeaveRequests()
      ]);

      setSession(detail);
      setData({
        notifications: notifs || [],
        photos: photos || [],
        evaluations: evals || [],
        attendance: attn || [],
        leaveRequests: (leaves || []).filter(l => l.session_id === parseInt(sessionId))
      });
    } catch (error) {
      console.error('获取社团空间数据失败:', error);
      Toast.fail('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sessionId, userInfo]);

  const handleApplyLeave = async () => {
    if (!leaveForm.reason.trim()) {
      Toast.info('请输入请假原因');
      return;
    }
    setSubmittingLeave(true);
    try {
      await applyForLeave({
        ...leaveForm,
        session_id: parseInt(sessionId),
        student_id: parseInt(studentId)
      });
      Toast.success('提交成功');
      setShowLeavePopup(false);
      fetchData();
    } catch (error) {
      Toast.fail('提交失败');
    } finally {
      setSubmittingLeave(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      Toast.info('请输入回复内容');
      return;
    }
    try {
      await replyToEvaluation(currentEval.id, replyText);
      Toast.success('回复成功');
      setShowReplyPopup(false);
      fetchData();
    } catch (error) {
      Toast.fail('回复失败');
    }
  };

  const renderAttendanceStatus = (status) => {
    const map = {
      present: { text: '出勤', color: '#00b578' },
      absent: { text: '缺勤', color: '#e11d48' },
      late: { text: '迟到', color: '#d97706' },
      excused: { text: '请假', color: '#056b4f' }
    };
    const s = map[status] || { text: status, color: '#94a3b8' };
    return <span style={{ color: s.color, fontWeight: 'bold' }}>{s.text}</span>;
  };

  const renderLeaveStatus = (status) => {
    const map = {
      pending: { text: '待审批', type: 'warning' },
      approved: { text: '已准假', type: 'success' },
      rejected: { text: '已驳回', type: 'danger' }
    };
    const s = map[status] || { text: status, type: 'default' };
    return <Tag type={s.type}>{s.text}</Tag>;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <NavBar 
        title={session?.club_name || '社团空间'} 
        leftText={<ArrowLeft size={20} />} 
        onClickLeft={() => navigate(-1)} 
        fixed 
        placeholder
      />

      <div style={{ 
        background: 'linear-gradient(180deg, var(--primary-green) 0%, #00b578 100%)',
        padding: '24px 20px 48px',
        color: '#fff'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ 
                width: 60, height: 60, borderRadius: 30, 
                background: 'rgba(255,255,255,0.2)', 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, border: '2px solid #fff'
            }}>
                {session?.club_name?.[0]}
            </div>
            <div>
                <div style={{ fontSize: 20, fontWeight: 'bold' }}>{session?.club_name}</div>
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                    {session?.instructor_name} 老师 | {session?.venue || '学校'}
                </div>
            </div>
        </div>
      </div>

      <div style={{ marginTop: -24, borderRadius: '24px 24px 0 0', background: '#f8fafc', overflow: 'hidden' }}>
        <Tabs active={activeTab} onChange={setActiveTab} sticky offsetTop={46} color="var(--primary-green)">
          <Tabs.TabPane title="通知" icon={<BullhornO />}>
            {loading ? <Skeleton row={10} style={{ padding: 20 }} /> : (
                <div style={{ padding: 16 }}>
                    {data.notifications.length === 0 ? <Empty description="暂无通知" /> : (
                        data.notifications.map(item => (
                            <div key={item.id} className="card" style={{ padding: 16, marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Tag type={item.msg_type === 'urgent' ? 'danger' : 'primary'} plain>
                                        {item.msg_type === 'urgent' ? '紧急' : '普通'}
                                    </Tag>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{dayjs(item.created_at).format('MM-DD')}</span>
                                </div>
                                <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 6 }}>{item.title}</div>
                                <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{item.content}</div>
                            </div>
                        ))
                    )}
                </div>
            )}
          </Tabs.TabPane>
          
          <Tabs.TabPane title="风采" icon={<PhotoO />}>
            {loading ? <Skeleton row={10} style={{ padding: 20 }} /> : (
                <div style={{ padding: 16 }}>
                    {data.photos.length === 0 ? <Empty description="相册还是空的" /> : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                            {data.photos.map(item => (
                                <div key={item.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                                    <Image 
                                      src={item.image_url} 
                                      fit="cover" 
                                      height="120" 
                                      onClick={() => {
                                        ImagePreview.open({
                                          images: data.photos.map(p => p.image_url),
                                          startPosition: data.photos.indexOf(item)
                                        });
                                      }}
                                    />
                                    <div style={{ padding: 8, fontSize: 12, color: '#475569' }}>{item.description || '社团瞬间'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane title="评价" icon={<MedalO />}>
            {loading ? <Skeleton row={10} style={{ padding: 20 }} /> : (
                <div style={{ padding: 16 }}>
                    {data.evaluations.length === 0 ? <Empty description="暂无评价" /> : (
                        data.evaluations.map(item => (
                            <div key={item.id} className="card" style={{ padding: 16, marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <div style={{ color: '#fa8c16', fontSize: 14 }}>
                                        {'★'.repeat(item.score)}{'☆'.repeat(5 - item.score)}
                                    </div>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{item.evaluation_date}</span>
                                </div>
                                <div style={{ fontSize: 15, color: '#0f172a', background: '#f8fafc', padding: 12, borderRadius: 8 }}>
                                    {item.comment || '教师未填写评语'}
                                </div>
                                {item.student_reply ? (
                                    <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '2px solid var(--primary-green)' }}>
                                        <div style={{ fontSize: 12, color: 'var(--primary-green)', fontWeight: 'bold', marginBottom: 4 }}>我的回复：</div>
                                        <div style={{ fontSize: 13, color: '#475569' }}>{item.student_reply}</div>
                                        <div 
                                            style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, textAlign: 'right' }}
                                            onClick={() => { setCurrentEval(item); setReplyText(item.student_reply); setShowReplyPopup(true); }}
                                        >
                                            修改回复
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ marginTop: 12, textAlign: 'right' }}>
                                        <Button size="mini" plain type="primary" icon={<ChatO />} onClick={() => { setCurrentEval(item); setReplyText(''); setShowReplyPopup(true); }}>回复老师</Button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane title="考勤" icon={<CalendarO />}>
            <div style={{ padding: 16 }}>
                <div className="card" style={{ padding: 20, textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 14, color: '#475569', marginBottom: 12 }}>如有出勤异常，请及时联系老师</div>
                    <Button 
                        type="primary" 
                        round 
                        block 
                        icon={<Edit />} 
                        style={{ background: '#056b4f', borderColor: '#056b4f' }}
                        onClick={() => setShowLeavePopup(true)}
                    >
                        在线提交请假申请
                    </Button>
                </div>

                {data.leaveRequests.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 15, fontWeight: 'bold', margin: '0 0 12px 4px', color: '#0f172a' }}>请假记录</div>
                        {data.leaveRequests.map(leave => (
                            <div key={leave.id} className="card" style={{ padding: 16, marginBottom: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>{leave.start_date} ~ {leave.end_date}</div>
                                    {renderLeaveStatus(leave.status)}
                                </div>
                                <div style={{ fontSize: 13, color: '#475569', marginTop: 8 }}>原因：{leave.reason}</div>
                                {leave.review_comment && (
                                    <div style={{ fontSize: 12, color: '#e11d48', marginTop: 4, background: '#fff2f0', padding: 6, borderRadius: 4 }}>
                                        批复：{leave.review_comment}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ fontSize: 15, fontWeight: 'bold', margin: '0 0 12px 4px', color: '#0f172a' }}>出勤历史</div>
                <div className="card" style={{ padding: 0 }}>
                    {data.attendance.length === 0 ? <Empty description="暂无考勤记录" /> : (
                        data.attendance.map((item, idx) => (
                            <Cell 
                                key={item.id}
                                title={item.attendance?.attendance_date}
                                value={renderAttendanceStatus(item.status)}
                                border={idx !== data.attendance.length - 1}
                            />
                        ))
                    )}
                </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>

      {/* 请假弹出层 */}
      <Popup 
        visible={showLeavePopup} 
        onClose={() => setShowLeavePopup(false)} 
        position="bottom" 
        round 
        style={{ height: '60%' }}
      >
        <div style={{ padding: '24px 20px' }}>
            <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 20, textAlign: 'center' }}>请假申请</div>
            <Field 
                label="开始日期" 
                value={leaveForm.start_date} 
                readonly 
                onClick={() => Toast.info('目前仅支持手动输入')} 
                onChange={v => setLeaveForm({...leaveForm, start_date: v})}
                placeholder="YYYY-MM-DD"
            />
            <Field 
                label="结束日期" 
                value={leaveForm.end_date} 
                onChange={v => setLeaveForm({...leaveForm, end_date: v})}
                placeholder="YYYY-MM-DD"
            />
            <Field 
                label="请假原因" 
                type="textarea" 
                rows={3} 
                value={leaveForm.reason} 
                onChange={v => setLeaveForm({...leaveForm, reason: v})}
                placeholder="请详细说明请假原因"
            />
            <div style={{ marginTop: 32 }}>
                <Button 
                    type="primary" 
                    round 
                    block 
                    loading={submittingLeave} 
                    onClick={handleApplyLeave}
                    style={{ background: 'var(--primary-green)', borderColor: 'var(--primary-green)' }}
                >
                    确认提交
                </Button>
            </div>
        </div>
      </Popup>

      {/* 回复弹出层 */}
      <Dialog
        visible={showReplyPopup}
        title="回复老师评价"
        showCancelButton
        onCancel={() => setShowReplyPopup(false)}
        onConfirm={handleReply}
      >
        <div style={{ padding: 20 }}>
            <Field
                type="textarea"
                rows={4}
                value={replyText}
                onChange={setReplyText}
                placeholder="写下您的感受或回复..."
                border
            />
        </div>
      </Dialog>
    </div>
  );
};

export default ClubSpace;
